import { useEffect, useState } from "react";
import SampleEditPage from "../../../pages/Lab Admin Tabs/RequestedList/SampleEditPage";
import {
  getSampleEditData,
  getUnitsData,
  getProductTrades,
  getProductsByTradeId,
  getFeedTypeByTradeAndProduct,
  getSpeciesByTradeAndProductAndFeed,
  getProductConfiguration,
  updateSampleEditData,
  getCompanyListData,
  getLocationsByCompanyId,
  getUsersByLocationId,
} from "../../../pages/Lab Admin Tabs/RequestedList/requestedListApi";
import ReusableModal from "../../atoms/modal/ReusableModal";
import { toast } from "react-toastify";

const SampleModalWrapper = ({
  sampleID,
  setSampleID,
  showModal,
  setShowModal,
  onUpdateSuccess,
}) => {
  const [editRowData, setEditRowData] = useState({});
  const [availableProductTrades, setAvailableProductTrades] = useState([]);
  const [availableProductTypes, setAvailableProductTypes] = useState([]);
  const [availableFeedTypes, setAvailableFeedTypes] = useState([]);
  const [availableSpecies, setAvailableSpecies] = useState([]);
  const [unitsOptions, setUnitsOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [customerOptionsByLocation, setCustomerOptionsByLocation] = useState([]);
  const [samples, setSamples] = useState([]);
  useEffect(() => {
    const fetchSampleData = async () => {
      try {
        const data = await getSampleEditData({ sampleID });
        await loadProductTypes(data.productTradeNameId);
        await loadFeedTypes(data.productTradeNameId, data.productTypeId);
        await loadSpecies(
          data.productTradeNameId,
          data.productTypeId,
          data.feedTypeId
        );
        setEditRowData(data);
      } catch (error) {
        setEditRowData(null);
        alert("Failed to fetch sample details.");
        console.error("Sample Edit fetch error:", error);
      }
    };
    if (sampleID) {
      fetchSampleData();
    }
  }, [sampleID]);
  useEffect(() => {
    const initializeDependentDropdowns = async () => {
      try {
        console.log("Starting dropdown initialization...");
        const [productTradesRes, unitsRes] = await Promise.all([
          getProductTrades(),
          getUnitsData(),
        ]);
        console.log("Product trades loaded:", productTradesRes);
        setAvailableProductTrades(productTradesRes || []);
        setUnitsOptions(
          unitsRes.map((ele) => ({ value: ele.unitId, label: ele.name })) || []
        );

        const pageSize = 500;
        const extractCompanies = (res) =>
          Array.isArray(res)
            ? res
            : Array.isArray(res?.results)
            ? res.results
            : Array.isArray(res?.data)
            ? res.data
            : [];

        // Fetch first page to get totalRecords
        const firstPageRes = await getCompanyListData({
          PageNumber: 1,
          PageSize: pageSize,
        });
        let allCompanies = extractCompanies(firstPageRes);
        const totalRecords = Number(firstPageRes?.totalRecords) || allCompanies.length;

        // Fetch remaining pages in parallel
        if (allCompanies.length < totalRecords) {
          const totalPages = Math.ceil(totalRecords / pageSize);
          const remainingPages = Array.from(
            { length: totalPages - 1 },
            (_, i) => i + 2
          );
          const remainingResults = await Promise.all(
            remainingPages.map((p) =>
              getCompanyListData({ PageNumber: p, PageSize: pageSize })
            )
          );
          for (const res of remainingResults) {
            allCompanies = allCompanies.concat(extractCompanies(res));
          }
        }

        //console.log("All companies before mapping:", allCompanies);
        const mappedCompanies = allCompanies
          .map((company) => ({
            value: company.companyId ?? company.companyID ?? company.id,
            label: company.name ?? company.companyName ?? "",
          }))
          .filter((company) => company.value && company.label);
        
        //console.log("Mapped and filtered companies:", mappedCompanies);
        setCompanyOptions(mappedCompanies);
      } catch (error) {
        console.error("Failed to initialize dependent dropdowns:", error);
        setCompanyOptions([]);
      }
    };
    initializeDependentDropdowns();
  }, []);

  useEffect(() => {
    if (!showModal) return;
    if (!companyOptions || companyOptions.length === 0) return;

    const hasCompanyId = !!(editRowData?.companyId ?? editRowData?.companyID);
    if (hasCompanyId) return;

    const companyName = editRowData?.company || editRowData?.companyName || "";
    if (!companyName) return;

    const matchedCompany = companyOptions.find(
      (company) =>
        String(company.label || "").toLowerCase() ===
        String(companyName).toLowerCase()
    );

    if (!matchedCompany?.value) return;

    setEditRowData((prev) => ({
      ...prev,
      companyId: matchedCompany.value,
      company: matchedCompany.label,
    }));
  }, [showModal, companyOptions, editRowData?.companyId, editRowData?.companyID, editRowData?.company, editRowData?.companyName]);

  useEffect(() => {
    const loadLocationsByCompany = async () => {
      const selectedCompanyId =
        editRowData?.companyId ?? editRowData?.companyID ?? "";
      if (!showModal || !selectedCompanyId) {
        setLocationOptions([]);
        return;
      }

      try {
        const locations = await getLocationsByCompanyId(selectedCompanyId);
        const normalizedLocations = Array.isArray(locations)
          ? locations
          : Array.isArray(locations?.results)
          ? locations.results
          : Array.isArray(locations?.data)
          ? locations.data
          : [];

        setLocationOptions(
          normalizedLocations
            .map((location) => ({
              value: location.locationId ?? location.id,
              label: location.name ?? location.locationName ?? "",
            }))
            .filter((location) => location.value && location.label)
        );
      } catch (error) {
        console.error("Failed to load locations by company:", error);
        setLocationOptions([]);
      }
    };

    loadLocationsByCompany();
  }, [showModal, editRowData?.companyId, editRowData?.companyID]);

  useEffect(() => {
    if (!showModal) return;
    if (!locationOptions || locationOptions.length === 0) return;
    if (editRowData?.locationId) return;

    const locationName = editRowData?.location || editRowData?.locationName || "";
    if (!locationName) return;

    const matchedLocation = locationOptions.find(
      (location) =>
        String(location.label || "").toLowerCase() ===
        String(locationName).toLowerCase()
    );

    if (!matchedLocation?.value) return;

    setEditRowData((prev) => ({
      ...prev,
      locationId: matchedLocation.value,
      locationID: matchedLocation.value,
      location: matchedLocation.label,
    }));
  }, [showModal, locationOptions, editRowData?.locationId, editRowData?.location, editRowData?.locationName]);

  useEffect(() => {
    if (!showModal) return;
    if (!Array.isArray(locationOptions)) return;
    if (locationOptions.length !== 1) return;

    const onlyLocation = locationOptions[0];
    const onlyLocationId = String(onlyLocation?.value ?? "");
    const currentLocationId = String(
      editRowData?.locationId ?? editRowData?.locationID ?? ""
    );

    if (!onlyLocationId) return;
    if (currentLocationId === onlyLocationId) return;

    setEditRowData((prev) => ({
      ...prev,
      locationId: onlyLocationId,
      locationID: onlyLocationId,
      location: onlyLocation.label || prev?.location || "",
      locationCustomerId: "",
      customerId: "",
      customerName: "",
    }));
  }, [showModal, locationOptions, editRowData?.locationId, editRowData?.locationID]);

  useEffect(() => {
    const loadCustomersByLocation = async () => {
      const selectedLocationId = editRowData?.locationId ?? editRowData?.locationID;
      if (!showModal || !selectedLocationId) {
        setCustomerOptionsByLocation([]);
        return;
      }

      try {
        const users = await getUsersByLocationId(selectedLocationId);
        const normalizedUsers = Array.isArray(users)
          ? users
          : Array.isArray(users?.results)
          ? users.results
          : Array.isArray(users?.data)
          ? users.data
          : [];

        setCustomerOptionsByLocation(
          normalizedUsers
            .map((user) => {
              const fullName =
                user.fullName ||
                `${user.firstName || ""} ${user.lastName || ""}`.trim();

              const normalizedCustomerId = String(
                user.userID ?? user.userId ?? user.customerId ?? user.id ?? ""
              );

              return {
                value: normalizedCustomerId,
                label: fullName || user.customerName || user.userName || user.email || "",
              };
            })
            .filter((user) => user.value && user.label)
        );
      } catch (error) {
        console.error("Failed to load customers by location:", error);
        setCustomerOptionsByLocation([]);
      }
    };

    loadCustomersByLocation();
  }, [showModal, editRowData?.locationId, editRowData?.locationID]);

  useEffect(() => {
    if (!showModal) return;
    if (!customerOptionsByLocation || customerOptionsByLocation.length === 0) return;
    if (editRowData?.locationCustomerId) return;

    const customerName =
      editRowData?.customerName ||
      editRowData?.customer ||
      editRowData?.userName ||
      editRowData?.fullName ||
      "";

    if (!customerName) return;

    const matchedCustomer = customerOptionsByLocation.find(
      (customer) =>
        String(customer.label || "").toLowerCase() ===
        String(customerName).toLowerCase()
    );

    if (!matchedCustomer?.value) return;

    setEditRowData((prev) => ({
      ...prev,
      locationCustomerId: String(matchedCustomer.value),
      customerId: String(matchedCustomer.value),
      customerName: matchedCustomer.label,
    }));
  }, [
    showModal,
    customerOptionsByLocation,
    editRowData?.locationCustomerId,
    editRowData?.customerName,
    editRowData?.customer,
    editRowData?.userName,
    editRowData?.fullName,
  ]);

  useEffect(() => {
    if (!showModal) return;

    const selectedLocationId =
      editRowData?.locationId ?? editRowData?.locationID ?? "";
    if (!selectedLocationId) return;

    if (!Array.isArray(customerOptionsByLocation)) return;
    if (customerOptionsByLocation.length !== 1) return;

    const onlyCustomer = customerOptionsByLocation[0];
    const onlyCustomerId = String(onlyCustomer?.value ?? "");
    const currentCustomerId = String(editRowData?.locationCustomerId ?? "");

    if (!onlyCustomerId) return;
    if (currentCustomerId === onlyCustomerId) return;

    setEditRowData((prev) => ({
      ...prev,
      locationCustomerId: onlyCustomerId,
      customerId: onlyCustomerId,
      customerName: onlyCustomer.label || prev?.customerName || "",
    }));
  }, [
    showModal,
    customerOptionsByLocation,
    editRowData?.locationId,
    editRowData?.locationID,
    editRowData?.locationCustomerId,
  ]);

  const loadProductTypes = async (productTradeId) => {
    if (!productTradeId) return;
    try {
      const productTypesData = await getProductsByTradeId(productTradeId);
      setAvailableProductTypes(
        productTypesData.map((ele) => ({
          label: ele.name,
          value: ele.productTypeId,
        })) || []
      );
      setEditRowData((prev) => ({
        ...prev,
        productTypeId: "",
        feedTypeId: "",
        speciesId: "",
        cfrMinLevel: "",
        cfrMaxLevel: "",
        loqValue: "",
      }));
    } catch (error) {
      console.error("Failed to load product types:", error);
      setAvailableProductTypes([]);
    }
  };

  const loadFeedTypes = async (productTradeId, productTypeId, sampleId) => {
    if (!productTradeId || !productTypeId) return;
    try {
      const feedTypesData = await getFeedTypeByTradeAndProduct(
        productTradeId,
        productTypeId
      );
      const mapped = feedTypesData.map((ele) => ({
          label: ele.feedTypeName,
          value: ele.feedTypeId,
          defaultSelection: ele.defaultSelection,
        })) || [];
      setAvailableFeedTypes(mapped);

      // Auto-select feed type with defaultSelection flag
      const defaultFeed = feedTypesData.find((f) => f.defaultSelection);
      if (defaultFeed) {
        const defaultFeedId = defaultFeed.feedTypeId;
        setEditRowData((prev) => ({
          ...prev,
          feedTypeId: defaultFeedId,
          speciesId: "",
        }));
        // Cascade: load species for the auto-selected feed type
        await loadSpecies(productTradeId, productTypeId, defaultFeedId);
      }
    } catch (error) {
      console.error("Failed to load feed types:", error);
      setAvailableFeedTypes([]);
    }
  };

  const loadSpecies = async (
    productTradeId,
    productTypeId,
    feedTypeId,
    sampleId
  ) => {
    if (!productTradeId || !productTypeId || !feedTypeId) return;
    try {
      const speciesData = await getSpeciesByTradeAndProductAndFeed(
        productTradeId,
        productTypeId,
        feedTypeId
      );
      setAvailableSpecies(
        speciesData.map((ele) => ({
          label: ele.speciesName,
          value: ele.speciesId,
          masterSpeciesId: ele.masterSpeciesId,
        })) || []
      );
    } catch (error) {
      console.error("Failed to load species:", error);
    }
  };

  const loadProductConfiguration = async (
    productTradeId,
    productTypeId,
    feedTypeId,
    speciesId
  ) => {
    if (!productTradeId || !productTypeId || !feedTypeId || !speciesId) return;
    try {
      const configData = await getProductConfiguration(
        productTradeId,
        productTypeId,
        feedTypeId,
        speciesId
      );

      setEditRowData((prev) => ({
        ...prev,
        cfrMinLevel: configData?.cfrMinLevel || prev.cfrMinLevel || "",
        cfrMaxLevel: configData?.cfrMaxLevel || prev.cfrMaxLevel || "",
        loqValue: configData?.loqValue || prev.loqValue || "",
      }));
    } catch (error) {
      console.error("Failed to load product configuration:", error);
    }
  };

  const handleEditDataChange = (updatedData) => {
    setEditRowData((prev) => {
      const next = {
        ...prev,
        ...updatedData,
      };

      if (
        Object.prototype.hasOwnProperty.call(updatedData, "companyId") &&
        String(updatedData.companyId) !== String(prev?.companyId || "")
      ) {
        next.locationId = "";
        next.location = "";
        next.locationCustomerId = "";
      }

      if (
        Object.prototype.hasOwnProperty.call(updatedData, "locationId") &&
        String(updatedData.locationId) !== String(prev?.locationId || "")
      ) {
        next.locationCustomerId = "";
      }

      return next;
    });
  };
  const handleSampleUpdate = async () => {
    try {
      const {
        sampleId,
        result,
        newComments,
        productTypeId,
        locationId,
        unitId,
        speciesId,
        companyId,
        lotNumber,
        customerSampleId,
        barcode,
        expectedLevel,
        cfrMaxLevel,
        cfrMinLevel,
        loqValue,
        productTypeName,
        productTradeNameId,
        productTradeName,
        feedTypeId,
        feedTypeName,
        speciesName,
        referenceNo,
        locationCustomerId,
        manufactureDate,
      } = editRowData;
      if (!newComments ) {
        toast.error("New Comments are required.");
        return;
      }
      const response = await updateSampleEditData({
        sampleId,
        assayResult: result,
        remarks: newComments,
        productTypeId: productTypeId,
        unitId,
        speciesId,
        locationId,
        companyId,
        cfrMaxLevel,
        cfrMinLevel,
        loqValue,
        lotNumber: lotNumber,
        customerSampleId: customerSampleId,
        barcodeNumber: barcode,
        expectedLevel: expectedLevel,
        productTypeName,
        productTradeNameId,
        productTradeName,
        feedTypeId,
        feedTypeName,
        speciesName,
        referenceNo,
        customerId: locationCustomerId,
        manufactureDate: manufactureDate || null,
      });
      toast.success("Sample updated successfully.");
      setShowModal(false);
      setEditRowData(null);
      onUpdateSuccess?.();
      return;
    } catch (error) {
      toast.error("Failed to update sample.");
    }
    setShowModal(false);
    setEditRowData(null);
  };

  return (
    <ReusableModal
      show={showModal}
      onHide={() => {
        setShowModal(false);
        setEditRowData(null);
        setSampleID(null);
      }}
      title={`Product : ${editRowData?.product || ""}`}
      size="xl"
      scrollable={true}
      buttons={[
        {
          text: "Cancel",
          variant: "outlined",
          onClick: () => {
            setShowModal(false);
            setEditRowData(null);
            setSampleID(null);
          },
        },
        {
          text: "Update",
          variant: "active",
          onClick: () => {
            handleSampleUpdate();
            setSampleID(null);
          },
        },
      ]}
    >
      {/* Render EditPage component and pass the selected row data as props */}
      <SampleEditPage
        data={editRowData}
        onDataChange={handleEditDataChange}
        companyOptions={companyOptions}
        locationOptions={locationOptions}
        customerOptionsByLocation={customerOptionsByLocation}
        productTypeOptions={availableProductTypes}
        speciesOptions={availableSpecies}
        unitsOptions={unitsOptions}
        availableProductTrades={availableProductTrades}
        availableFeedTypes={availableFeedTypes}
        loadProductTypes={loadProductTypes}
        loadFeedTypes={loadFeedTypes}
        loadSpecies={loadSpecies}
        loadProductConfiguration={loadProductConfiguration}
        samples={samples}
      />
    </ReusableModal>
  );
};

export default SampleModalWrapper;
